import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

// Annotation ("Events") dot colors: black = primary period, grey = compare period
const EVENT_PRIMARY_COLOR = 'rgba(0, 0, 0, 1)'
const EVENT_COMPARE_COLOR = 'rgba(150, 150, 150, 1)'

// Max note rows shown per source group (primary/compare) before collapsing
// the remainder into a single "+N more note(s)" row.
const MAX_NOTE_ROWS = 3

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const shortDate = (iso) => {
  if (!iso) return ''
  const [, month, day] = iso.split('-').map(Number)
  return `${MONTHS[month - 1]} ${day}`
}
const prefixDate = (date, text) => (date ? `${date}: ${text}` : text)

export default class extends Controller {
  static targets = ['canvas']
  static values = {
    data: Object,
    options: { type: Object, default: {} },
    annotations: { type: Array, default: [] },
    editUrl: { type: String, default: '' },
    buckets: { type: Array, default: [] }
  }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    this.destroyChart()
  }

  initializeChart() {
    if (!this.hasCanvasTarget) return

    // Destroy any existing chart on this canvas
    const existing = Chart.getChart(this.canvasTarget)
    if (existing) existing.destroy()
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    // Hide any tooltip left over from the previous chart instance
    if (this.tooltipEl) this.tooltipEl.style.opacity = '0'

    const chartData = JSON.parse(JSON.stringify(this.dataValue))

    // Add annotation points if present
    if (this.annotationsValue.length > 0) {
      chartData.datasets.push(this.buildAnnotationDataset(chartData.labels))
    }

    this.chart = new Chart(this.canvasTarget, {
      type: 'line',
      data: chartData,
      options: this.buildOptions()
    })
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    if (this.tooltipEl) {
      this.tooltipEl.remove()
      this.tooltipEl = null
    }
  }

  buildAnnotationDataset(labels) {
    const annotations = this.annotationsValue
    const primaryColor = EVENT_PRIMARY_COLOR
    const compareColor = EVENT_COMPARE_COLOR

    const data = labels.map((_, i) => {
      const hasAnnotation = annotations.some((a) => a.index === i)
      return hasAnnotation ? 0.01 : null
    })

    // Compare-only indices render as hollow grey dots (matching the legend
    // swatch); any index with a primary annotation renders solid black
    const isCompareOnly = (i) =>
      data[i] !== null &&
      annotations
        .filter((a) => a.index === i)
        .every((a) => a.source === 'compare')
    const fillAt = (i) => {
      if (data[i] === null) return 'transparent'
      return isCompareOnly(i) ? 'transparent' : primaryColor
    }
    const borderAt = (i) => {
      if (data[i] === null) return 'transparent'
      return isCompareOnly(i) ? compareColor : primaryColor
    }

    return {
      label: 'Events',
      data: data,
      // Its own scale, so the dots stay pinned to the bottom whatever the
      // metric axes are doing — including when every one of them is hidden.
      yAxisID: 'yEvents',
      borderColor: primaryColor,
      backgroundColor: primaryColor,
      pointRadius: data.map((v) => (v !== null ? 6 : 0)),
      pointBackgroundColor: data.map((_, i) => fillAt(i)),
      pointBorderColor: data.map((_, i) => borderAt(i)),
      pointBorderWidth: data.map((_, i) => (isCompareOnly(i) ? 2 : 1)),
      pointHoverRadius: data.map((v) => (v !== null ? 8 : 0)),
      pointHoverBackgroundColor: data.map((_, i) => fillAt(i)),
      showLine: false,
      hidden: false,
      // Dots sit at the bottom of their scale; without this the lower half is clipped
      clip: false,
      // Draw above metric datasets so note dots stay visible/clickable at y=0
      order: -1
    }
  }

  buildOptions() {
    const annotations = this.annotationsValue
    const options = JSON.parse(JSON.stringify(this.optionsValue))

    if (!options.scales) options.scales = {}
    // 0-1 with the dots at 0.01: the event row sits on the chart floor and does
    // not move when a metric is toggled off.
    options.scales.yEvents = { type: 'linear', display: false, min: 0, max: 1 }

    // The left axis owns the horizontal grid, so when every series on it is
    // hidden the right axis has to take the grid over or the chart loses it.
    if (options.scales.y1) {
      options.scales.y1.grid = {
        ...(options.scales.y1.grid || {}),
        drawOnChartArea: (ctx) =>
          !ctx.chart
            .getSortedVisibleDatasetMetas()
            .some((meta) => (meta.yScale ? meta.yScale.id : meta.yAxisID) === 'y')
      }
    }

    if (!options.plugins) options.plugins = {}
    options.plugins.legend = {
      ...(options.plugins.legend || {}),
      onClick: (_event, legendItem) => this.toggleFromLegend(legendItem.datasetIndex),
      labels: {
        // Square swatches instead of the default 40px-wide rectangles
        boxWidth: 12,
        boxHeight: 12,
        // Every series keeps its legend entry, struck through while hidden —
        // the legend is a control, and an entry that disappears when clicked
        // cannot be clicked back on.
        // Compare-period event dots render grey; add a legend entry for them.
        // It maps to the same Events dataset, so clicking toggles all dots.
        generateLabels: (chart) => {
          const items =
            Chart.defaults.plugins.legend.labels.generateLabels(chart)
          const hasCompareEvents = annotations.some(
            (a) => a.source === 'compare'
          )
          if (!hasCompareEvents) return items

          const eventsIndex = chart.data.datasets.findIndex(
            (ds) => ds.label === 'Events'
          )
          if (eventsIndex === -1) return items

          const compareItem = {
            text: 'Events (Compare)',
            fillStyle: 'transparent',
            strokeStyle: EVENT_COMPARE_COLOR,
            lineWidth: 2,
            // Same dash pattern the compare datasets use (borderDash [5, 5])
            lineDash: [5, 5],
            datasetIndex: eventsIndex,
            hidden: !chart.isDatasetVisible(eventsIndex)
          }

          // Slot it directly after the Events entry
          const eventsItemIndex = items.findIndex(
            (item) => item.datasetIndex === eventsIndex
          )
          items.splice(eventsItemIndex + 1, 0, compareItem)
          return items
        }
      }
    }
    // Chart.js draws its default tooltip on the canvas, so long note lists
    // get clipped at the chart edge. Render an HTML tooltip instead: it can
    // overflow the chart area and gives each line a period color box.
    options.plugins.tooltip = {
      enabled: false,
      mode: 'index',
      intersect: false,
      external: (context) => this.renderTooltip(context)
    }

    if (this.editUrlValue) {
      options.onClick = (event, _elements, chart) =>
        this.handleAnnotationClick(event, chart)
      options.onHover = (event, _elements, chart) => {
        const overDot = this.eventDotAt(event, chart) !== undefined
        const target = event.native ? event.native.target : event.target
        if (target) target.style.cursor = overDot ? 'pointer' : ''
      }
    }

    return options
  }

  renderTooltip({ chart, tooltip }) {
    const el = this.tooltipElement(chart)

    if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
      el.style.opacity = '0'
      return
    }

    el.replaceChildren()
    ;(tooltip.title || []).forEach((line) => {
      const title = document.createElement('div')
      title.textContent = line
      title.style.fontWeight = 'bold'
      title.style.marginBottom = '4px'
      el.appendChild(title)
    })
    this.tooltipRows(chart, tooltip.dataPoints).forEach((row) => {
      el.appendChild(this.buildTooltipRow(row))
    })

    this.positionTooltip(el, chart, tooltip)
  }

  // One row per metric value; Events expand to one row per note line.
  // Metric data always comes first, note rows last (the Events dataset's
  // order: -1 would otherwise sort them to the top). Compare-period rows get
  // the legend's compare style: colorless box, dotted border.
  // Rows are {text, fill, border, dotted}.
  tooltipRows(chart, dataPoints) {
    const rows = []
    const noteRows = []

    dataPoints.forEach((dp) => {
      const ds = chart.data.datasets[dp.datasetIndex]
      if (ds.label === 'Events') {
        noteRows.push(...this.annotationRows(dp.dataIndex))
        return
      }

      const compare = Array.isArray(ds.borderDash)
      // Datasets from BaseReportService carry their own suffix; y1 is no longer
      // rate-only, so the axis alone cannot say whether a value is a percentage.
      const suffix =
        ds.valueSuffix !== undefined
          ? ds.valueSuffix
          : ds.yAxisID === 'y1'
            ? '%'
            : ''
      rows.push({
        text: `${ds.label}: ${dp.parsed.y}${suffix}`,
        fill: compare ? 'transparent' : ds.borderColor,
        border: ds.borderColor,
        dotted: compare
      })
    })

    return rows.concat(noteRows)
  }

  annotationRows(dataIndex) {
    const matched = this.annotationsValue.filter((a) => a.index === dataIndex)
    const rows = []

    const styleFor = (source) =>
      source === 'compare'
        ? { fill: 'transparent', border: EVENT_COMPARE_COLOR, dotted: true }
        : { fill: EVENT_PRIMARY_COLOR, border: EVENT_PRIMARY_COLOR, dotted: false }

    const formatGroup = (group, source) => {
      const groupRows = []

      const titleLower = (a) => (a.title || '').toLowerCase()
      const emailCampaigns = group.filter((a) =>
        titleLower(a).includes('email campaign')
      )
      const smsCampaigns = group.filter((a) =>
        titleLower(a).includes('sms campaign')
      )
      const others = group.filter(
        (a) =>
          !titleLower(a).includes('email campaign') &&
          !titleLower(a).includes('sms campaign')
      )

      // Campaigns collapse into one count per day so weekly buckets stay readable
      const pushDailyCounts = (campaigns, label) => {
        const counts = new Map()
        campaigns.forEach((a) => {
          const date = shortDate(a.deployed_at)
          counts.set(date, (counts.get(date) || 0) + 1)
        })
        counts.forEach((count, date) => {
          groupRows.push({
            text: prefixDate(date, `${label} (${count})`),
            ...styleFor(source)
          })
        })
      }

      pushDailyCounts(emailCampaigns, 'Email Campaigns')
      pushDailyCounts(smsCampaigns, 'SMS Campaigns')
      others.forEach((a) => {
        groupRows.push({
          text: prefixDate(shortDate(a.deployed_at), a.title),
          ...styleFor(source)
        })
      })

      // Cap this source group's rows; collapse the remainder into one summary
      // row so a busy YoY hover doesn't grow unbounded. Full detail stays
      // reachable via the notes modal / clicking the Events dot.
      rows.push(...groupRows.slice(0, MAX_NOTE_ROWS))
      const overflow = groupRows.length - MAX_NOTE_ROWS
      if (overflow > 0) {
        rows.push({
          text: `+${overflow} more note(s)`,
          ...styleFor(source)
        })
      }
    }

    const primaryAnnotations = matched.filter(
      (a) => !a.source || a.source === 'primary'
    )
    const compareAnnotations = matched.filter((a) => a.source === 'compare')

    if (primaryAnnotations.length > 0) formatGroup(primaryAnnotations, 'primary')
    if (compareAnnotations.length > 0) formatGroup(compareAnnotations, 'compare')

    return rows
  }

  buildTooltipRow({ text, fill, border, dotted }) {
    const row = document.createElement('div')
    row.style.display = 'flex'
    row.style.alignItems = 'center'
    row.style.gap = '6px'
    row.style.margin = '2px 0'

    const box = document.createElement('span')
    box.style.flex = '0 0 auto'
    box.style.width = '10px'
    box.style.height = '10px'
    box.style.backgroundColor = fill
    box.style.border = `2px ${dotted ? 'dotted' : 'solid'} ${border}`
    row.appendChild(box)

    const label = document.createElement('span')
    label.textContent = text
    row.appendChild(label)

    return row
  }

  tooltipElement(chart) {
    if (this.tooltipEl && this.tooltipEl.isConnected) return this.tooltipEl

    const el = document.createElement('div')
    el.style.position = 'absolute'
    el.style.pointerEvents = 'none'
    el.style.background = 'rgba(0, 0, 0, 0.85)'
    el.style.color = '#fff'
    el.style.borderRadius = '4px'
    el.style.padding = '8px 10px'
    el.style.fontSize = '12px'
    el.style.maxWidth = '320px'
    el.style.zIndex = '10'
    el.style.opacity = '0'
    el.style.transition = 'opacity 0.15s ease'
    chart.canvas.parentNode.appendChild(el)
    this.tooltipEl = el
    return el
  }

  positionTooltip(el, chart, tooltip) {
    el.style.opacity = '1'

    const area = chart.canvas.parentNode

    // Placed beside the hover column (never centered on it) so the hovered
    // points and vertical slice stay visible. Prefer the right side; flip to
    // the left when it would overflow the chart area.
    let left = tooltip.caretX + 16
    if (left + el.offsetWidth > area.clientWidth) {
      left = Math.max(0, tooltip.caretX - el.offsetWidth - 16)
    }

    // Anchor on the metric lines: exclude the Events dot (pinned at y≈0)
    // from the average so the tooltip lands on the line, not the event.
    const lineElements = (tooltip.dataPoints || [])
      .filter((dp) => chart.data.datasets[dp.datasetIndex].label !== 'Events')
      .map((dp) => dp.element)
    const anchorY = lineElements.length
      ? lineElements.reduce((sum, e) => sum + e.y, 0) / lineElements.length
      : tooltip.caretY

    // Vertically centered on the anchor, clamped to the chart area. A
    // tooltip taller than the chart pins to the top and overflows downward —
    // the chart area doesn't clip, so every line stays visible.
    const top = Math.min(
      Math.max(0, anchorY - el.offsetHeight / 2),
      Math.max(0, area.clientHeight - el.offsetHeight)
    )

    el.style.left = `${left}px`
    el.style.top = `${top}px`
  }

  // Precise hit test (interaction mode is 'index', so the elements passed to
  // onClick/onHover cover the whole x column, not just the clicked point)
  eventDotAt(event, chart) {
    // 'point' returns every element under the cursor (not just the single
    // nearest), so an overlapping metric dot can't shadow an Events dot
    const hits = chart.getElementsAtEventForMode(
      event,
      'point',
      { intersect: true },
      false
    )
    return hits.find(
      (el) => chart.data.datasets[el.datasetIndex].label === 'Events'
    )
  }

  handleAnnotationClick(event, chart) {
    const point = this.eventDotAt(event, chart)
    if (!point) return

    const matched = this.annotationsValue.filter(
      (a) => a.index === point.index && a.id
    )
    if (matched.length === 0) return

    window.dispatchEvent(
      new CustomEvent('conversion-note:edit', {
        detail: { annotations: matched }
      })
    )
  }

  toggleDataset(event) {
    if (!this.chart) return

    this.setDatasetsVisibility(
      this.indicesFor(event.target),
      event.target.checked
    )
    this.updateSelectAllCheckbox()
  }

  indicesFor(checkbox) {
    const indicesRaw = checkbox.dataset.datasetIndices
    return indicesRaw
      ? indicesRaw
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !Number.isNaN(n))
      : [parseInt(checkbox.dataset.datasetIndex, 10)]
  }

  // The legend and the summary table toggle the same series, so a legend click
  // runs through the checkbox that owns it: the box unticks, and the metric's
  // compare series goes with it the way it does from the table.
  toggleFromLegend(index) {
    const isVisible = !this.chart.isDatasetVisible(index)
    const checkbox = Array.from(
      this.element.querySelectorAll('.dataset-checkbox')
    ).find((box) => this.indicesFor(box).includes(index))

    if (!checkbox) {
      this.setDatasetsVisibility([index], isVisible)
      return
    }

    checkbox.checked = isVisible
    this.setDatasetsVisibility(this.indicesFor(checkbox), isVisible)
    this.updateSelectAllCheckbox()
  }

  toggleAllDatasets(event) {
    if (!this.chart) return

    const isVisible = event.target.checked
    const allIndices = this.chart.data.datasets
      .map((ds, index) => (ds.label === 'Events' ? null : index))
      .filter((i) => i !== null)

    this.setDatasetsVisibility(allIndices, isVisible)

    const checkboxes = this.element.querySelectorAll('.dataset-checkbox')
    checkboxes.forEach((checkbox) => {
      checkbox.checked = isVisible
    })
  }

  setDatasetsVisibility(indices, isVisible) {
    indices.forEach((index) => {
      this.chart.data.datasets[index].hidden = !isVisible
      this.chart.setDatasetVisibility(index, isVisible)
    })
    this.chart.update()
  }

  updateSelectAllCheckbox() {
    const checkboxes = this.element.querySelectorAll('.dataset-checkbox')
    const selectAllCheckbox = this.element.querySelector(
      '#toggle-all-datasets'
    )

    if (!selectAllCheckbox || checkboxes.length === 0) return

    const checkedCount = Array.from(checkboxes).filter(
      (cb) => cb.checked
    ).length

    if (checkedCount === 0) {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
    } else if (checkedCount === checkboxes.length) {
      selectAllCheckbox.checked = true
      selectAllCheckbox.indeterminate = false
    } else {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = true
    }
  }

  noteSaved(event) {
    const comment = event.detail.comment
    const annotations = this.annotationsValue.filter((a) => a.id !== comment.id)

    this.bucketsValue.forEach((bucket) => {
      if (
        bucket.index < this.dataValue.labels.length &&
        bucket.start <= comment.deployed_at &&
        comment.deployed_at <= bucket.end
      ) {
        annotations.push({
          id: comment.id,
          index: bucket.index,
          label: this.dataValue.labels[bucket.index],
          title: comment.title,
          body: comment.body,
          date: comment.date,
          deployed_at: comment.deployed_at,
          source: bucket.source
        })
      }
    })

    this.annotationsValue = annotations
  }

  noteDeleted(event) {
    const id = event.detail.id
    this.annotationsValue = this.annotationsValue.filter((a) => a.id !== id)
  }

  dataValueChanged() {
    this.initializeChart()
  }

  optionsValueChanged() {
    this.initializeChart()
  }

  annotationsValueChanged() {
    this.initializeChart()
  }
}
